/**
 * Tutorial Configuration (AC-665, AC-702)
 *
 * Central hub that imports per-tutorial step definitions and provides
 * infrastructure maps, catalog entries, and helper functions.
 *
 * Tutorial definitions live in ./tutorials/:
 * 1. "core_workflow" (18 steps) - Movie Summarizer: Project -> Agent -> Workflow
 * 2. "book_analyzer" (12 steps) - Book Analyzer with schemas
 * 3. "deterministic_story_flow" (20 steps) - 3-agent pipeline with models and conditionals
 * 4. "template_web_research" (12 steps) - Web Research using agent templates
 * 5. "mcp_browser_intro" (12 steps) - Introduction to MCP Servers via Browser Automation
 * 6. "agents_as_tools" (17 steps) - Agents as Tools: Translation Hub with orchestrator and tool agents
 * 7. "email_sorter" (31 steps) - Basic Agentic Email Sorter with two-agent triage, conditionals, and scheduling
 * 8. "financial_research_agent" (23 steps) - Financial Research: nested models, parallel fan-out, web search, agents-as-tools
 *
 * Step Targeting:
 * - Uses data-tutorial attributes on target elements
 * - Attributes must be added to Sidebar.tsx, ProjectsPage.tsx, AgentsPage.tsx, etc.
 */

import { TutorialStep } from '../types/tutorial';

// =============================================================================
// Tutorial Imports
// =============================================================================

import { TUTORIAL_VALUES, TUTORIAL_STEPS } from './tutorials/coreWorkflow';
import { DETERMINISTIC_TUTORIAL_VALUES, DETERMINISTIC_TUTORIAL_STEPS } from './tutorials/deterministicStoryFlow';
import { BOOK_ANALYZER_TUTORIAL_STEPS } from './tutorials/bookAnalyzer';
import { TEMPLATE_TUTORIAL_VALUES, TEMPLATE_TUTORIAL_STEPS } from './tutorials/templateWebResearch';
import { MCP_BROWSER_TUTORIAL_VALUES, MCP_BROWSER_TUTORIAL_STEPS } from './tutorials/mcpBrowserIntro';
import { AGENTS_AS_TOOLS_TUTORIAL_VALUES, AGENTS_AS_TOOLS_TUTORIAL_STEPS } from './tutorials/agentsAsTools';
import { EMAIL_SORTER_TUTORIAL_VALUES, EMAIL_SORTER_TUTORIAL_STEPS } from './tutorials/emailSorter';
import { FINANCIAL_RESEARCH_TUTORIAL_VALUES, FINANCIAL_RESEARCH_TUTORIAL_STEPS } from './tutorials/financialResearchAgent';

// Re-export values so existing imports continue to work
export { TUTORIAL_VALUES, TUTORIAL_STEPS } from './tutorials/coreWorkflow';
export { DETERMINISTIC_TUTORIAL_VALUES, DETERMINISTIC_TUTORIAL_STEPS } from './tutorials/deterministicStoryFlow';
export { BOOK_ANALYZER_TUTORIAL_STEPS } from './tutorials/bookAnalyzer';
export { TEMPLATE_TUTORIAL_VALUES, TEMPLATE_TUTORIAL_STEPS } from './tutorials/templateWebResearch';
export { MCP_BROWSER_TUTORIAL_VALUES, MCP_BROWSER_TUTORIAL_STEPS } from './tutorials/mcpBrowserIntro';
export { AGENTS_AS_TOOLS_TUTORIAL_VALUES, AGENTS_AS_TOOLS_TUTORIAL_STEPS } from './tutorials/agentsAsTools';
export { EMAIL_SORTER_TUTORIAL_VALUES, EMAIL_SORTER_TUTORIAL_STEPS } from './tutorials/emailSorter';
export { FINANCIAL_RESEARCH_TUTORIAL_VALUES, FINANCIAL_RESEARCH_TUTORIAL_STEPS } from './tutorials/financialResearchAgent';

// =============================================================================
// Multi-Tutorial Infrastructure
// =============================================================================

/**
 * Default number of total steps in the core workflow tutorial.
 */
export const TOTAL_TUTORIAL_STEPS = TUTORIAL_STEPS.length;

/**
 * Tutorial ID constants.
 */
export const TUTORIAL_ID = 'core_workflow';
export const BOOK_ANALYZER_TUTORIAL_ID = 'book_analyzer';
export const DETERMINISTIC_TUTORIAL_ID = 'deterministic_story_flow';
export const TEMPLATE_TUTORIAL_ID = 'template_web_research';
export const MCP_BROWSER_TUTORIAL_ID = 'mcp_browser_intro';
export const AGENTS_AS_TOOLS_TUTORIAL_ID = 'agents_as_tools';
export const EMAIL_SORTER_TUTORIAL_ID = 'email_sorter';
export const FINANCIAL_RESEARCH_TUTORIAL_ID = 'financial_research_agent';

/**
 * Map of tutorial IDs to their expected workflow names.
 * Used to find the correct workflow when resuming a tutorial.
 */
export const TUTORIAL_WORKFLOW_NAMES: Record<string, string> = {
  [TUTORIAL_ID]: TUTORIAL_VALUES.orchestration.name,
  [BOOK_ANALYZER_TUTORIAL_ID]: 'Book Analyzer',
  [DETERMINISTIC_TUTORIAL_ID]: DETERMINISTIC_TUTORIAL_VALUES.orchestration.name,
  [TEMPLATE_TUTORIAL_ID]: TEMPLATE_TUTORIAL_VALUES.orchestration.name,
  [MCP_BROWSER_TUTORIAL_ID]: MCP_BROWSER_TUTORIAL_VALUES.orchestration.name,
  [AGENTS_AS_TOOLS_TUTORIAL_ID]: AGENTS_AS_TOOLS_TUTORIAL_VALUES.orchestration.name,
  [EMAIL_SORTER_TUTORIAL_ID]: EMAIL_SORTER_TUTORIAL_VALUES.orchestration.name,
  [FINANCIAL_RESEARCH_TUTORIAL_ID]: FINANCIAL_RESEARCH_TUTORIAL_VALUES.orchestration.name,
};

export const TUTORIAL_STEP_MAP: Record<string, TutorialStep[]> = {
  [TUTORIAL_ID]: TUTORIAL_STEPS,
  [BOOK_ANALYZER_TUTORIAL_ID]: BOOK_ANALYZER_TUTORIAL_STEPS,
  [DETERMINISTIC_TUTORIAL_ID]: DETERMINISTIC_TUTORIAL_STEPS,
  [TEMPLATE_TUTORIAL_ID]: TEMPLATE_TUTORIAL_STEPS,
  [MCP_BROWSER_TUTORIAL_ID]: MCP_BROWSER_TUTORIAL_STEPS,
  [AGENTS_AS_TOOLS_TUTORIAL_ID]: AGENTS_AS_TOOLS_TUTORIAL_STEPS,
  [EMAIL_SORTER_TUTORIAL_ID]: EMAIL_SORTER_TUTORIAL_STEPS,
  [FINANCIAL_RESEARCH_TUTORIAL_ID]: FINANCIAL_RESEARCH_TUTORIAL_STEPS,
};

/**
 * Map of tutorial IDs to their total step counts.
 */
export const TUTORIAL_TOTAL_MAP: Record<string, number> = {
  [TUTORIAL_ID]: TUTORIAL_STEPS.length,
  [BOOK_ANALYZER_TUTORIAL_ID]: BOOK_ANALYZER_TUTORIAL_STEPS.length,
  [DETERMINISTIC_TUTORIAL_ID]: DETERMINISTIC_TUTORIAL_STEPS.length,
  [TEMPLATE_TUTORIAL_ID]: TEMPLATE_TUTORIAL_STEPS.length,
  [MCP_BROWSER_TUTORIAL_ID]: MCP_BROWSER_TUTORIAL_STEPS.length,
  [AGENTS_AS_TOOLS_TUTORIAL_ID]: AGENTS_AS_TOOLS_TUTORIAL_STEPS.length,
  [EMAIL_SORTER_TUTORIAL_ID]: EMAIL_SORTER_TUTORIAL_STEPS.length,
  [FINANCIAL_RESEARCH_TUTORIAL_ID]: FINANCIAL_RESEARCH_TUTORIAL_STEPS.length,
};

/**
 * Map of tutorial IDs to the step number where the workflow is created.
 * When resuming and the expected workflow is missing (e.g. user deleted it),
 * the tutorial resets to this step so the user can recreate it.
 */
export const TUTORIAL_WORKFLOW_RESTART_STEP: Record<string, number> = {
  [TUTORIAL_ID]: 9, // core_workflow creates workflow at step 9
  [BOOK_ANALYZER_TUTORIAL_ID]: 2,
  [DETERMINISTIC_TUTORIAL_ID]: 2,
  [TEMPLATE_TUTORIAL_ID]: 2,
  [MCP_BROWSER_TUTORIAL_ID]: 2,
  [AGENTS_AS_TOOLS_TUTORIAL_ID]: 7, // agents_as_tools creates workflow at step 7
  [EMAIL_SORTER_TUTORIAL_ID]: 7, // email_sorter creates workflow at step 7
  [FINANCIAL_RESEARCH_TUTORIAL_ID]: 2, // financial_research_agent creates workflow at step 2
};

/**
 * Get a tutorial step by step number (1-indexed).
 * @param stepNumber - The step number (1-indexed)
 * @param tutorialId - The tutorial ID (defaults to core_workflow)
 * @returns The tutorial step or undefined if not found
 */
export function getTutorialStep(
  stepNumber: number,
  tutorialId: string = TUTORIAL_ID
): TutorialStep | undefined {
  const steps = TUTORIAL_STEP_MAP[tutorialId];
  return steps?.find(step => step.stepNumber === stepNumber);
}

/**
 * Get the current step configuration based on step index (0-indexed).
 * @param index - The step index (0-indexed)
 * @param tutorialId - The tutorial ID (defaults to core_workflow)
 * @returns The tutorial step or undefined if not found
 */
export function getTutorialStepByIndex(
  index: number,
  tutorialId: string = TUTORIAL_ID
): TutorialStep | undefined {
  const steps = TUTORIAL_STEP_MAP[tutorialId];
  return steps?.[index];
}

// =============================================================================
// Tutorial Sections (for Bootcamp page grouping)
// =============================================================================

/**
 * Tutorial section definition for grouping tutorials on the Bootcamp page.
 */
export interface TutorialSection {
  id: string;
  title: string;
  description: string;
  /** The project name this section's tutorials use. Tutorials in this section auto-create this project if missing. */
  projectName: string;
  /** Project description used when auto-creating. */
  projectDescription: string;
}

/**
 * Defined tutorial sections.
 */
export const TUTORIAL_SECTIONS: TutorialSection[] = [
  {
    id: 'getting_started',
    title: 'Getting Started',
    description: 'Learn the fundamentals of Agent Commander by building your first agents and workflows.',
    projectName: TUTORIAL_VALUES.project.name,
    projectDescription: TUTORIAL_VALUES.project.description,
  },
  {
    id: 'advanced_workflows',
    title: 'Advanced Workflows',
    description: 'Master complex multi-agent pipelines, conditional logic, and structured output.',
    projectName: 'Advanced Tutorials',
    projectDescription: 'Tutorial projects for learning advanced multi-agent workflows, conditional logic, and structured output.',
  },
  {
    id: 'email_mcp',
    title: 'Email MCP',
    description: 'Build AI-powered email automation using the Email MCP server.',
    projectName: 'Email Tutorials',
    projectDescription: 'Tutorial projects for learning email automation with the Email MCP server.',
  },
];

/**
 * Map of section IDs to their section definitions.
 */
export const TUTORIAL_SECTION_MAP: Record<string, TutorialSection> = Object.fromEntries(
  TUTORIAL_SECTIONS.map(s => [s.id, s])
);

// =============================================================================
// Tutorial Catalog
// =============================================================================

/**
 * Tutorial catalog entry for the Bootcamp page tutorials listing.
 */
export interface TutorialCatalogEntry {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  estimatedMinutes: number;
  stepCount: number;
  prerequisite?: string;
  /** If true, navigate to the user's current project on start so project-scoped sidebar items are visible. */
  requiresOperation?: boolean;
  /** If set, this tutorial is locked until the specified tutorial ID is completed. */
  requiresTutorial?: string;
  /** Section this tutorial belongs to. */
  section: string;
}

/**
 * Catalog of all available tutorials displayed on the Bootcamp page.
 */
export const TUTORIAL_CATALOG: TutorialCatalogEntry[] = [
  {
    id: 'core_workflow',
    title: 'Build a Movie Summarizer',
    description:
      'Learn the basics by creating an agent that writes hilarious movie summaries. ' +
      'Create a project, configure an agent, build a workflow, and run it.',
    difficulty: 'beginner',
    estimatedMinutes: 10,
    stepCount: TUTORIAL_STEPS.length,
    section: 'getting_started',
  },
  {
    id: 'template_web_research',
    title: 'Web Research with Templates',
    description:
      'Learn how to use agent templates to quickly add pre-built agents to your workflows. ' +
      'Build a web research workflow using the Web Research Agent template.',
    difficulty: 'beginner',
    estimatedMinutes: 8,
    stepCount: TEMPLATE_TUTORIAL_STEPS.length,
    prerequisite:
      'Complete "Build a Movie Summarizer" first to have a project ready.',
    requiresOperation: true,
    section: 'getting_started',
  },
  {
    id: 'book_analyzer',
    title: 'Analyze a Book with Schemas',
    description:
      'Learn how to use schemas for structured agent output. Create a workflow that takes a book title ' +
      'and returns a detailed analysis with genre, characters, plot, and more. ' +
      'Learn to create agents and schemas directly from the workflow editor.',
    difficulty: 'beginner',
    estimatedMinutes: 10,
    stepCount: BOOK_ANALYZER_TUTORIAL_STEPS.length,
    prerequisite:
      'Complete "Build a Movie Summarizer" first to have a project ready.',
    requiresOperation: true,
    section: 'getting_started',
  },
  {
    id: 'agents_as_tools',
    title: 'Agents as Tools',
    description:
      'Learn the agents-as-tools pattern by building a Translation Hub. ' +
      'Create specialized translator agents and wire them as tools into an orchestrator ' +
      'that automatically delegates based on user requests.',
    difficulty: 'beginner',
    estimatedMinutes: 12,
    stepCount: AGENTS_AS_TOOLS_TUTORIAL_STEPS.length,
    prerequisite:
      'Complete "Build a Movie Summarizer" first to have a project ready.',
    requiresOperation: true,
    section: 'getting_started',
  },
  {
    id: 'mcp_browser_intro',
    title: 'Introduction to MCP Servers',
    description:
      'Learn what MCP servers are by building a browser automation workflow. ' +
      'Use the Web Browser Agent template to navigate websites, extract content, and take screenshots.',
    difficulty: 'beginner',
    estimatedMinutes: 10,
    stepCount: MCP_BROWSER_TUTORIAL_STEPS.length,
    prerequisite:
      'Complete "Web Research with Templates" first to have the Web Browser Agent template ready.',
    requiresOperation: true,
    requiresTutorial: TEMPLATE_TUTORIAL_ID,
    section: 'getting_started',
  },
  {
    id: 'deterministic_story_flow',
    title: 'Deterministic Story Flow',
    description:
      'Build a 3-agent pipeline with structured output models and conditional logic.',
    difficulty: 'intermediate',
    estimatedMinutes: 15,
    stepCount: DETERMINISTIC_TUTORIAL_STEPS.length,
    section: 'advanced_workflows',
  },
  {
    id: 'financial_research_agent',
    title: 'Financial Research Agent',
    description:
      'Build a financial research pipeline with nested schemas, parallel fan-out, web search, and agents-as-tools.',
    difficulty: 'advanced',
    estimatedMinutes: 20,
    stepCount: FINANCIAL_RESEARCH_TUTORIAL_STEPS.length,
    section: 'advanced_workflows',
  },
  {
    id: 'email_sorter',
    title: 'Basic Agentic Email Sorter',
    description:
      'Build a two-agent email sorting pipeline with structured output schemas, conditional branching, and scheduling.',
    difficulty: 'advanced',
    estimatedMinutes: 20,
    stepCount: EMAIL_SORTER_TUTORIAL_STEPS.length,
    section: 'email_mcp',
  },
];

export default TUTORIAL_STEPS;
